import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import './site-hero-slider.js';
import './site-feature-box.js';
import { store } from '../lib/store.js';

export class SitePageRenderer extends LitElement {
  static properties = {
    content: { type: Object },
    title: { type: String },
  };

  static styles = css`
    :host {
      display: block;
      color: var(--color-text-primary, #f0f0f5);
    }
    .page {
      min-height: 60vh;
    }
    .page__header {
      padding: var(--space-20, 5rem) var(--content-padding, 2rem) var(--space-12, 3rem);
      max-width: var(--content-max-width, 1440px);
      margin: 0 auto;
      text-align: center;
    }
    .page__title {
      margin: 0 0 var(--space-4, 1rem);
      font-size: var(--text-4xl, 2.5rem);
      font-weight: var(--font-extrabold, 800);
      line-height: var(--leading-tight, 1.1);
    }
    .page__subtitle {
      max-width: 720px;
      margin: 0 auto;
      color: var(--color-text-secondary, #a0a0b8);
      font-size: var(--text-lg, 1.125rem);
      line-height: var(--leading-relaxed, 1.7);
    }
    .elements {
      display: grid;
      gap: var(--space-12, 3rem);
      padding: 0 var(--content-padding, 2rem) var(--space-20, 5rem);
      max-width: var(--content-max-width, 1440px);
      margin: 0 auto;
    }
    .elements--flush {
      max-width: none;
      padding: 0 0 var(--space-20, 5rem);
    }
    .text {
      max-width: 850px;
      margin: 0 auto;
      color: var(--color-text-secondary, #a0a0b8);
      line-height: var(--leading-relaxed, 1.7);
    }
    .text h1, .text h2, .text h3, .text h4 {
      color: var(--color-text-primary, #f0f0f5);
      margin: 0;
    }
    .text--center {
      text-align: center;
    }
    .text--right {
      text-align: right;
    }
    .rich {
      max-width: 850px;
      margin: 0 auto;
      color: var(--color-text-secondary, #a0a0b8);
      line-height: var(--leading-relaxed, 1.7);
    }
    .rich img, .image img {
      display: block;
      width: 100%;
      border-radius: var(--border-radius, 0.75rem);
    }
    .image {
      max-width: 960px;
      margin: 0 auto;
    }
    .image figcaption {
      margin-top: var(--space-3, 0.75rem);
      color: var(--color-text-muted, #6b6b80);
      text-align: center;
      font-size: var(--text-sm, 0.875rem);
    }
    .grid {
      display: grid;
      gap: var(--space-6, 1.5rem);
      grid-template-columns: repeat(var(--columns, 3), minmax(0, 1fr));
    }
    .grid__item {
      min-width: 0;
    }
    .grid__image {
      width: 100%;
      aspect-ratio: 16 / 9;
      object-fit: cover;
      border-radius: var(--border-radius, 0.75rem);
      margin-bottom: var(--space-4, 1rem);
    }
    .grid__link, .button {
      display: inline-flex;
      width: fit-content;
      align-items: center;
      justify-content: center;
      min-height: 2.75rem;
      padding: 0 var(--space-5, 1.25rem);
      border-radius: var(--border-radius-sm, 0.375rem);
      text-decoration: none;
      font-weight: var(--font-semibold, 600);
      background: var(--color-cta, #ff6b35);
      color: white;
    }
    .button--secondary {
      background: transparent;
      color: var(--color-accent, #00d4ff);
      border: 1px solid var(--color-accent, #00d4ff);
    }
    .container, .row, .columns {
      display: grid;
      gap: var(--space-8, 2rem);
    }
    .container--hero {
      padding-top: var(--space-16, 4rem);
      padding-bottom: var(--space-16, 4rem);
    }
    .container--narrow {
      max-width: 900px;
      margin: 0 auto;
    }
    .row {
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      align-items: start;
    }
    .columns {
      grid-template-columns: repeat(var(--columns, 2), minmax(0, 1fr));
    }
    @media (max-width: 768px) {
      .row, .columns {
        grid-auto-flow: row;
        grid-template-columns: 1fr;
      }
      .grid {
        grid-template-columns: 1fr;
      }
      .page__title {
        font-size: var(--text-3xl, 2rem);
      }
    }
  `;

  render() {
    const content = normalizeContent(this.content, this.title);
    const hasHero = content.elements.some((element) => element.type === 'slideshow');

    return html`
      <article class="page">
        ${hasHero ? '' : html`
          <header class="page__header">
            <h1 class="page__title">${content.meta.headline}</h1>
            ${content.meta.subtitle ? html`<p class="page__subtitle">${content.meta.subtitle}</p>` : ''}
          </header>
        `}
        <div class="elements ${hasHero ? 'elements--flush' : ''}">
          ${content.elements.map((element) => this._renderElement(element))}
        </div>
      </article>
    `;
  }

  _renderElement(element) {
    switch (element.type) {
      case 'slideshow':
        return html`<site-hero-slider .slides=${element.props.slides || []}></site-hero-slider>`;
      case 'text':
        return renderText(element.props);
      case 'rich_text':
        return html`<div class="rich">${unsafeHTML(element.props.html || '')}</div>`;
      case 'image':
        return html`
          <figure class="image">
            <img src=${element.props.src || ''} alt=${element.props.alt || ''}>
            ${element.props.caption ? html`<figcaption>${element.props.caption}</figcaption>` : ''}
          </figure>
        `;
      case 'grid':
        return this._renderGrid(element.props);
      case 'button':
        return html`<a class="button button--${element.props.variant || 'primary'}" href=${element.props.href || '#'}>${element.props.label || ''}</a>`;
      case 'container':
        return html`<section class="container container--${element.props.variant || 'default'}">${(element.children || []).map((child) => this._renderElement(child))}</section>`;
      case 'row':
        return html`<div class="row">${(element.children || []).map((child) => this._renderElement(child))}</div>`;
      case 'columns':
        return html`<div class="columns" style=${`--columns:${element.props.columns || 2}`}>${(element.children || []).map((child) => this._renderElement(child))}</div>`;
      default:
        return '';
    }
  }

  _renderGrid(props) {
    const columns = Math.max(1, Math.min(Number(props.columns || 3), 4));
    return html`
      <div class="grid" style=${`--columns:${columns}`}>
        ${(props.items || []).map((item) => html`
          <div class="grid__item">
            ${item.image ? html`<img class="grid__image" src=${item.image} alt=${item.title || ''}>` : ''}
            <site-feature-box
              icon=${item.icon || 'default'}
              title=${item.title || ''}
              description=${item.description || ''}
            ></site-feature-box>
            ${item.link ? html`<a class="grid__link" href=${item.link}>${item.linkText || store.getSiteSettings()?.interface?.default_link_text || ''}</a>` : ''}
          </div>
        `)}
      </div>
    `;
  }
}

function renderText(props) {
  const tag = props.tag || 'p';
  const className = `text text--${props.align || 'left'}`;
  switch (tag) {
    case 'h1':
      return html`<div class=${className}><h1>${props.text || ''}</h1></div>`;
    case 'h2':
      return html`<div class=${className}><h2>${props.text || ''}</h2></div>`;
    case 'h3':
      return html`<div class=${className}><h3>${props.text || ''}</h3></div>`;
    case 'h4':
      return html`<div class=${className}><h4>${props.text || ''}</h4></div>`;
    default:
      return html`<p class=${className}>${props.text || ''}</p>`;
  }
}

function normalizeContent(content, title) {
  if (content?.schema_version === 1 && Array.isArray(content.elements)) {
    return content;
  }
  return {
    schema_version: 1,
    meta: {
      headline: content?.headline || title || __SITE_CONFIG__.name || 'Website',
      subtitle: content?.subtitle || '',
      description: content?.description || content?.subtitle || '',
    },
    elements: [],
  };
}

customElements.define('site-page-renderer', SitePageRenderer);
